import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const STATUS_META = {
  pending_manager: { label: 'Pending Manager',  color: '#92400E', bg: '#FEF3C7', dot: '#F59E0B' },
  pending_hr:      { label: 'Pending HR',        color: '#1D4ED8', bg: '#EFF6FF', dot: '#3B82F6' },
  approved:        { label: 'Approved',           color: '#065F46', bg: '#D1FAE5', dot: '#10B981' },
  rejected:        { label: 'Rejected',           color: '#991B1B', bg: '#FEE2E2', dot: '#EF4444' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending_hr;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, background: m.bg, color: m.color, padding: '0.2rem 0.6rem', borderRadius: '0.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, display: 'inline-block' }} />
      {m.label}
    </span>
  );
}

function AttendanceApproval() {
  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [rejectModal, setRejectModal]   = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving]             = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getPendingManualAttendance();
      setRecords(r.data || []);
    } catch { setRecords([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterStatus ? records.filter(r => r.approval_status === filterStatus) : records;

  const handleApprove = async (id) => {
    try { await hrms.approveManualAttendance(id); load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed to approve.'); }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      await hrms.rejectManualAttendance(rejectModal, rejectReason);
      setRejectModal(null); setRejectReason(''); load();
    } catch { alert('Failed to reject.'); } finally { setSaving(false); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
  const tdStyle = { padding: '0.75rem 1rem', fontSize: '0.82rem', color: theme.text, borderBottom: `1px solid ${theme.border}`, verticalAlign: 'middle' };
  const thStyle = { padding: '0.65rem 1rem', fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', background: '#F8FAFC', borderBottom: `2px solid ${theme.border}`, textAlign: 'left', whiteSpace: 'nowrap' };

  const pendingManagerCount = records.filter(r => r.approval_status === 'pending_manager').length;
  const pendingHRCount      = records.filter(r => r.approval_status === 'pending_hr').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Attendance Approval</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            All pending manual check-in requests
            {pendingManagerCount > 0 && <span style={{ marginLeft: '0.5rem', background: '#FEF3C7', color: '#92400E', padding: '0.1rem 0.5rem', borderRadius: '0.25rem', fontWeight: 600, fontSize: '0.75rem' }}>{pendingManagerCount} awaiting manager</span>}
            {pendingHRCount > 0 && <span style={{ marginLeft: '0.4rem', background: '#EFF6FF', color: '#1D4ED8', padding: '0.1rem 0.5rem', borderRadius: '0.25rem', fontWeight: 600, fontSize: '0.75rem' }}>{pendingHRCount} awaiting HR</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '0.45rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', background: '#fff' }}
          >
            <option value="">All Status</option>
            <option value="pending_manager">Pending Manager</option>
            <option value="pending_hr">Pending HR</option>
          </select>
          <Button variant="outlineGray" size="sm" onClick={load}>↻ Refresh</Button>
        </div>
      </div>

      <Card padding="0">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Emp Code', 'Employee', 'Department', 'Date', 'Check-In', 'Check-Out', 'Reporting Manager', 'Status', 'Actions'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '3rem' }}>Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>No pending manual attendance records</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '3rem', color: theme.textMuted }}>No records match the selected filter</td></tr>
              ) : filtered.map(r => (
                <tr key={r.id}
                  style={{ background: r.approval_status === 'pending_manager' ? '#FFFDF5' : '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = r.approval_status === 'pending_manager' ? '#FFFDF5' : '#fff'}>
                  <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontWeight: 600, color: theme.primary, fontSize: '0.78rem' }}>{r.emp_code}</span></td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{r.employee_name}</td>
                  <td style={{ ...tdStyle, color: theme.textMuted }}>{r.department || '—'}</td>
                  <td style={tdStyle}>{r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#059669' }}>{r.check_in || '—'}</td>
                  <td style={tdStyle}>{r.check_out || '—'}</td>
                  <td style={tdStyle}>
                    {r.approval_status === 'pending_manager'
                      ? r.reporting_manager_name
                        ? <span style={{ fontSize: '0.78rem', color: '#92400E', fontWeight: 500 }}>⏳ {r.reporting_manager_name}</span>
                        : <span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>No manager assigned</span>
                      : r.manager_approved_by_name
                        ? <span style={{ fontSize: '0.78rem', color: '#1D4ED8', fontWeight: 500 }}>✓ {r.manager_approved_by_name}</span>
                        : <span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>—</span>
                    }
                  </td>
                  <td style={tdStyle}><StatusBadge status={r.approval_status} /></td>
                  <td style={tdStyle}>
                    {r.approval_status === 'pending_hr' ? (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button size="sm" variant="success" onClick={() => handleApprove(r.id)}>✓ Approve</Button>
                        <Button size="sm" variant="danger" onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>✗ Reject</Button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>Awaiting manager</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={!!rejectModal}
        onClose={() => { setRejectModal(null); setRejectReason(''); }}
        title="Reject Manual Check-In"
        footer={<><Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button><Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button></>}
      >
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.35rem' }}>Reason (optional)</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
      </Modal>
    </div>
  );
}

export default AttendanceApproval;
